import { ArrowDownloadFilled, OpenFilled } from '@fluentui/react-icons';
import { isTDocument, TDocument, TVersion } from '@modules/documents';
import { TFile, useSignFileVersion } from '@modules/files';
import { TReplica } from '@modules/replicas';
import { ButtonLoading } from '@shared/components';
import { useCallback, useMemo } from 'react';
import { ApprovalStatus, TApproval } from '../types';
import {
  handlePdfBlob,
  EHandlePdfBlobMode,
  getPdfFilename,
  findItemVersion,
  isDocumentItemVersion,
  isTDraft,
} from '@modules/items';
import { TPermissions } from '@modules/access';
import { useExport } from '../../documents/api';

type TProps = {
  item: TDocument | TFile;
  approval: TApproval;
  permissions: TPermissions;
  replica?: TReplica;
};

const ViewPdfButton = ({ item, approval, permissions, replica }: TProps) => {
  const itemVersion = useMemo(() => findItemVersion(item, approval.versionId), [item.versions, approval?.versionId]);
  const version = itemVersion?.version;
  const isApproved = approval.status === ApprovalStatus.APPROVED;
  const pdfFilename = getPdfFilename((replica || item).name, item.pdfExportFileNames?.[approval.versionId]);

  // Both mutations will sign the version if it is not signed yet
  const signFileVersionMutation = useSignFileVersion();
  const exportDocumentMutation = useExport();

  // approved but not signed yet OR for some reason the PDF is not available (e.g. still being generated)
  const isPdfAvailable = Boolean(
    version &&
      (!isApproved || version.signature) &&
      (isDocumentItemVersion(itemVersion) && !isTDraft(itemVersion.version)
        ? itemVersion.version.isPdfUpToDate
        : version.pdfBlobName)
  );
  const handleViewPdf = useCallback(async () => {
    // not expected
    if (!version) {
      console.error('[DEBUG] No version found for this approval');
      return;
    }

    let updatedVersion = version;
    if (!isPdfAvailable) {
      if (isTDocument(item))
        updatedVersion = (await exportDocumentMutation.mutateAsync({
          documentId: item._id,
          versionId: version.id,
        })) as TVersion;
      else updatedVersion = await signFileVersionMutation.mutateAsync({ id: item._id, versionId: version.id });
    }

    handlePdfBlob(
      item._id,
      updatedVersion.pdfBlobName!,
      permissions.token,

      // If the PDF is not available, we need to download it. We lose user-gesture after an await, which causes the browser to block window.open.
      isPdfAvailable ? EHandlePdfBlobMode.VIEW : EHandlePdfBlobMode.DOWNLOAD,
      pdfFilename
    );
  }, [item, version, isPdfAvailable, pdfFilename, permissions.token]);

  /**
   * Only show "View PDF variant" for the most recent approval of this version.
   * Older approvals (accessible via the Approvals tab) should not display the button.
   */
  const lastApproval = useMemo(
    () => item.approvals.filter((a) => a.versionId === version?.id).pop(),
    [item.approvals, version?.id]
  );
  if (lastApproval?._id !== approval._id) return null;

  return (
    <ButtonLoading
      as="a"
      onClick={handleViewPdf}
      loading={signFileVersionMutation.isPending || exportDocumentMutation.isPending}
      icon={isPdfAvailable ? <OpenFilled /> : <ArrowDownloadFilled />}
      size="large"
      appearance="primary"
      className="w-full"
    >
      {`${isPdfAvailable ? 'View' : 'Download'} ${isApproved ? 'signed PDF' : 'PDF variant'}`}
    </ButtonLoading>
  );
};

export default ViewPdfButton;
